import { defineComponent as _defineComponent } from 'vue'
import { ref, computed } from 'vue';
import {
    getDeliveryInternalOrderColumns,
    getSelectedOrderColumns,
} from '@/modules/Logistics/helpers/LogisticsTableHelper';
import { createDeliveryOrderTableRows } from '@/modules/Logistics/helpers/DeliveryOrderHelper';
import { onProjectRowEvent } from '@/modules/Logistics/helpers/DeliveryOrderNavigationHelper';
import type {
    DeliveryOrder,
    DeliveryOrderTableRow,
    DeliveryRegionOption,
} from '@/modules/Logistics/interfaces/DeliveryOrderInterfaces';


export default /*#__PURE__*/_defineComponent({
  __name: 'InternalOrderSelector',
  props: {
    orders: null,
    selectedInternalOrderIds: null,
    lockedInternalOrderIds: null,
    deliveryRegions: null,
    orderStatuses: null
  },
  emits: ["selection-change"],
  setup(__props: any, { emit }: { emit: ({
    (e: 'selection-change', internalOrderIds: number[]): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
    orders: DeliveryOrder[];
    selectedInternalOrderIds: number[];
    lockedInternalOrderIds: number[];
    deliveryRegions: DeliveryRegionOption[];
    orderStatuses: { id: string; name: string; code: string }[];
};





const expandedOrderIds = ref<string[]>(props.orders.map((order) => order.id));
const selectedDeliveryRegionId = ref<string | undefined>();

const lockedInternalOrderIdSet = computed(() => new Set(props.lockedInternalOrderIds));
const tableOrders = computed(() => createDeliveryOrderTableRows(props.orders, props.orderStatuses));
const filteredTableOrders = computed(() =>
    filterOrdersByDeliveryRegionName(tableOrders.value, selectedDeliveryRegionId.value),
);

const totalInternalOrderCount = computed(() =>
    props.orders.reduce((count, order) => count + order.projectInternalOrders.length, 0),
);

const selectedInternalOrderCount = computed(() => props.selectedInternalOrderIds.length);

const selectionPercentage = computed(() =>
    totalInternalOrderCount.value
        ? Math.min((selectedInternalOrderCount.value / totalInternalOrderCount.value) * 100, 100)
        : 0,
);

const orderColumns = computed(() => getSelectedOrderColumns());

// Filters already-loaded orders locally by the delivery-region column name; it never makes an API request.
function filterOrdersByDeliveryRegionName(
    orders: DeliveryOrderTableRow[],
    deliveryRegionId?: string,
): DeliveryOrderTableRow[] {
    if (!deliveryRegionId) {
        return orders;
    }

    const selectedRegion = props.deliveryRegions.find((region) => region.id === deliveryRegionId);
    if (!selectedRegion) {
        return orders;
    }

    const normalizedRegionName = selectedRegion.name.trim().toLocaleLowerCase();

    return orders.filter((order) => order.deliveryRegion.trim().toLocaleLowerCase().includes(normalizedRegionName));
}

function handleDeliveryRegionChange(deliveryRegionId?: string) {
    selectedDeliveryRegionId.value = deliveryRegionId;
}

function getInternalOrderColumns(order: DeliveryOrder) {
    return getDeliveryInternalOrderColumns(order);
}

function getInternalOrderSelection(order: DeliveryOrder) {
    const orderInternalIds = new Set(order.projectInternalOrders.map((internalOrder) => Number(internalOrder.id)));

    return {
        selectedRowKeys: props.selectedInternalOrderIds.filter((id) => orderInternalIds.has(id)),
        getCheckboxProps: (internalOrder: { id: string | number }) => ({
            disabled: lockedInternalOrderIdSet.value.has(Number(internalOrder.id)),
        }),
        onChange: (keys: Array<string | number>) => {
            const otherOrderIds = props.selectedInternalOrderIds.filter((id) => !orderInternalIds.has(id));
            const lockedOrderIds = props.lockedInternalOrderIds.filter((id) => orderInternalIds.has(id));
            emit('selection-change', [...new Set([...otherOrderIds, ...lockedOrderIds, ...keys.map(Number)])]);
        },
    };
}

function toggleExpand(order: DeliveryOrder) {
    const index = expandedOrderIds.value.indexOf(order.id);
    if (index >= 0) {
        expandedOrderIds.value.splice(index, 1);
    } else {
        expandedOrderIds.value.push(order.id);
    }
}

function getCompletionRowClass(rowType: 'order' | 'internal') {
    return (order: { isCompleted: boolean }): string => {
        const completionClass = order.isCompleted ? 'delivery-order-row--completed' : 'delivery-order-row--incomplete';

        return rowType === 'order' ? `${completionClass} delivery-order-row--clickable` : completionClass;
    };
}

return { __sfc: true,props, emit, expandedOrderIds, selectedDeliveryRegionId, lockedInternalOrderIdSet, tableOrders, filteredTableOrders, totalInternalOrderCount, selectedInternalOrderCount, selectionPercentage, orderColumns, filterOrdersByDeliveryRegionName, handleDeliveryRegionChange, getInternalOrderColumns, getInternalOrderSelection, toggleExpand, getCompletionRowClass, onProjectRowEvent }
}

})